import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Col, Row } from 'react-bootstrap';

import AppLayout from '../../../layouts/AppLayout/AppLayout';
import { NioButton, NioCard, NioSection } from '../../../components';
import api, { mergeStoredUser } from '../../../utils/api';
import { useTranslation } from '../../../i18n/I18nProvider';

function getStoredUser() {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function Profile() {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const [user, setUser] = useState(getStoredUser);
  const [formData, setFormData] = useState(getStoredUser);
  const [isEditing, setIsEditing] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingVerificationDocs, setDeletingVerificationDocs] = useState(false);
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentsError, setPaymentsError] = useState('');
  const [showAllPayments, setShowAllPayments] = useState(false);
  const didFetchRef = useRef(false);
  const photoInputRef = useRef(null);

  const loadPayments = useCallback(async (email, limit = 5) => {
    if (!email) return;
    setPaymentsLoading(true);
    setPaymentsError('');
    try {
      const res = await api.getMyPayments(email, { limit });
      if (res?.success && Array.isArray(res.data)) {
        setPayments(res.data);
      } else if (Array.isArray(res)) {
        setPayments(res);
      }
    } catch {
      setPaymentsError(t('profile.paymentsError'));
    } finally {
      setPaymentsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    // En dev avec React StrictMode, les effets peuvent être exécutés deux fois.
    // On évite donc de déclencher 2 fois la récupération du profil.
    if (didFetchRef.current) return;
    didFetchRef.current = true; 

    const u = getStoredUser();
    if (!u) {
      navigate('/auth/login', { replace: true });
      return;
    }
    setUser(u);
    setFormData(u);
    const userId = u.id || u._id;
    if (userId) {
      setLoadingProfile(true);
      api.getProfile(userId)
        .then((res) => {
          if (res && res.success && res.data) {
            const fullProfile = res.data;
            setUser(fullProfile);
            setFormData(fullProfile);
            mergeStoredUser(fullProfile);
            if (fullProfile.email) loadPayments(fullProfile.email);
            return;
          }
          // Si 404, tenter un fallback par email (utile si l'ID localStorage est obsolète)
          if (res === null && u.email) {
            return api.getProfileByEmail(u.email).then((res2) => {
              if (res2 && res2.success && res2.data) {
                const fullProfile = res2.data;
                setUser(fullProfile);
                setFormData(fullProfile);
                mergeStoredUser(fullProfile);
                return;
              }
              // Si toujours introuvable : nettoyer et rediriger
              localStorage.removeItem('user');
              setUser(null);
              navigate('/auth/login', { replace: true });
            });
          }
          // Si 404 et pas d'email : nettoyer et rediriger
          if (res === null) {
            localStorage.removeItem('user');
            setUser(null);
            navigate('/auth/login', { replace: true });
          }
        })
        .catch(() => {
          // Erreur réseau ou serveur : garder les données du localStorage
        })
        .finally(() => setLoadingProfile(false));
    } else {
      setLoadingProfile(false);
    }
    if (u.email) loadPayments(u.email);
  }, [navigate, loadPayments]);

  // "Mes paiements" du header pointe vers /profile#payments : défiler jusqu'à la section.
  useEffect(() => {
    if (window.location.hash === '#payments' && !loadingProfile) {
      document.getElementById('payments')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [loadingProfile]);

  const handleLogout = () => {
    localStorage.removeItem('user');
    setUser(null);
    navigate('/');
  };

  const handleStartEdit = () => {
    if (!user) return;
    setFormData(user);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setFormData(user);
    setIsEditing(false);
  };

  const handleSaveEdit = async () => {
    if (!user || !formData) return;
    const userId = user.id || user._id;
    if (!userId) return;
    setSaving(true);
    try {
      const payload = { ...formData };
      // Ne jamais envoyer password depuis cette page
      delete payload.password;
      const res = await api.updateProfile(userId, payload);
      if (res && res.success && res.data) {
        const updated = res.data;
        setUser(updated);
        setFormData(updated);
        mergeStoredUser(updated);
        setIsEditing(false);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Update profile failed', e);
    } finally {
      setSaving(false);
    }
  };

  const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handlePickPhoto = () => {
    if (photoInputRef.current) photoInputRef.current.click();
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      const dataUrl = await readFileAsDataUrl(file);
      const userId = (user && (user.id || user._id)) || null;
      if (!userId) return;

      // Aperçu immédiat
      setUser((prev) => ({ ...(prev || {}), photo: dataUrl }));
      setFormData((prev) => ({ ...(prev || {}), photo: dataUrl }));

      // Sauvegarde immédiate sans passer par "Modifier mon profil"
      setSaving(true);
      const res = await api.updateProfile(userId, { photo: dataUrl });
      if (res && res.success && res.data) {
        const updated = res.data;
        setUser(updated);
        setFormData(updated);
        mergeStoredUser(updated);
      }
    } catch {
      // ignore read errors
    } finally {
      setSaving(false);
      e.target.value = '';
    }
  };

  if (!user) {
    return null;
  }

  if (loadingProfile) {
    return (
      <AppLayout title={t('profile.accountTitle')} rootClass="layout-1">
        <NioSection className="pt-120 pt-lg-160">
          <Row className="justify-content-center">
            <Col lg={10}>
              <NioCard className="shadow-sm">
                <NioCard.Body className="p-5 text-center text-muted">
                  {t('profile.loading')}
                </NioCard.Body>
              </NioCard>
            </Col>
          </Row>
        </NioSection>
      </AppLayout>
    );
  }

  const displayName = [user.name, user.lastName].filter(Boolean).join(' ') || user.email || t('profile.roles.user');

  const hasValue = (value) => {
    if (value == null) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'object') return Object.keys(value).length > 0;
    return true;
  };

  const renderValue = (value) => {
    if (value == null || value === '') return t('profile.emptyValue');
    if (Array.isArray(value)) {
      const parts = value
        .map((v) => (v == null ? '' : String(v)))
        .map((s) => s.trim())
        .filter(Boolean);
      return parts.length ? parts.join(', ') : t('profile.emptyValue');
    }

    if (typeof value === 'object') {
      try {
        return JSON.stringify(value);
      } catch {
        return String(value);
      }
    }

    const str = String(value);
    if (str.startsWith('http')) {
      return (
        <a href={str} target="_blank" rel="noopener noreferrer">
          {str}
        </a>
      );
    }
    return str;
  };

  const Field = ({ label, value, name, type = 'text' }) => {
    const currentValue =
      isEditing && name && formData
        ? formData[name]
        : value;

    // En mode affichage, ne montrer que les champs réellement remplis
    if (!isEditing && !hasValue(currentValue)) return null;

    const handleChange = (e) => {
      const newValue = e.target.value;
      if (!name) return;
      setFormData((prev) => ({
        ...(prev || {}),
        [name]: newValue,
      }));
    };

    return (
      <Col xs={12} sm={6} key={label}>
        <div className="border-bottom border-light pb-2 mb-3">
          <span className="text-muted small text-uppercase">{label}</span>
          {isEditing && name ? (
            <input
              type={type}
              className="form-control mt-1"
              value={currentValue || ''}
              onChange={handleChange}
            />
          ) : (
            <p className="mb-0 mt-1">{renderValue(currentValue)}</p>
          )}
        </div>
      </Col>
    );
  };

  const Section = ({ title, children }) => (
    <div className="mb-4">
      <h6 className="text-uppercase text-primary border-bottom pb-2 mb-3">{title}</h6>
      <Row>{children}</Row>
    </div>
  );

  const roleLabel = t(`profile.roles.${user.role}`) || user.role;
  const userTypeLabel = t(`profile.userTypes.${user.userType}`) || user.userType;

  const knownKeys = new Set([
    'name', 'lastName', 'civilite', 'email', 'phone', 'role', 'userType',
    'region', 'city', 'address', 'location', 'sector', 'subSector', 'creationYear',
    'registrationNumber', 'vatNumber', 'numCin', 'companyType', 'legalForm',
    'businessName', 'experience', 'qualifications', 'description', 'serviceId',
    'serviceCategory', 'skills', 'pricing', 'photo', 'schedule',
    'paymentMethod', 'coverageGuarantees',
    'portfolioPhotos', 'portfolioProjects', 'portfolioVideos',
    'about', 'clientCommitments',
    'linkedin', 'facebook', 'twitter', 'instagram', 'website', 'password',
    'createdAt', 'id', '_id', '__v',
  ]);
  const otherFields = Object.entries(user).filter(
    ([key]) => !knownKeys.has(key) && key !== 'password' && key !== 'verificationDocuments'
  );
  const otherFieldsWithValue = otherFields.filter(([, value]) => hasValue(value));
  const providerInfoHasValue = [
    // Identity
    user.civilite, user.name, user.lastName, user.email, user.phone,
    // Localisation
    user.region, user.city, user.address, user.location,
    // Prestataire
    user.userType, user.serviceId, user.serviceCategory, user.sector, user.subSector, user.creationYear,
    user.registrationNumber, user.vatNumber, user.numCin, user.companyType, user.legalForm, user.businessName,
    user.experience, user.qualifications, user.description, user.skills, user.about, user.clientCommitments,
    user.pricing, user.schedule, user.coverageGuarantees, user.paymentMethod,
    user.portfolioPhotos, user.portfolioVideos, user.portfolioProjects,
    // Réseaux
    user.linkedin, user.facebook, user.twitter, user.instagram, user.website,
  ].some(hasValue);
  const formatKey = (key) => key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()).trim();
  const formatVal = (v) => (v && typeof v === 'object' && v.toString ? v.toString() : v);
  const formatDate = (v) => {
    if (!v) return t('profile.emptyValue');
    try {
      const d = new Date(v);
      return isNaN(d.getTime()) ? String(v) : d.toLocaleDateString(language, { dateStyle: 'medium' });
    } catch {
      return String(v);
    }
  };

  const zoneIntervention =
    user?.coverageGuarantees ||
    user?.location ||
    [user?.city, user?.region].filter(Boolean).join(', ');

  const portfolioSummaryParts = [
    user?.portfolioPhotos?.length ? t('profile.portfolioPhotos', { count: user.portfolioPhotos.length }) : '',
    user?.portfolioVideos?.length ? t('profile.portfolioVideos', { count: user.portfolioVideos.length }) : '',
    user?.portfolioProjects?.length ? t('profile.portfolioProjects', { count: user.portfolioProjects.length }) : '',
  ].filter(Boolean);

  const portfolioSummary = portfolioSummaryParts.length ? portfolioSummaryParts.join(', ') : '';

  const handleDeleteVerificationDocuments = async () => {
    if (!user) return;
    const userId = user.id || user._id;
    if (!userId) return;
    const docs = Array.isArray(user.verificationDocuments) ? user.verificationDocuments : [];
    if (!docs.length) return;

    const ok = window.confirm(t('profile.deleteVerificationDocsConfirm'));
    if (!ok) return;

    setDeletingVerificationDocs(true);
    try {
      const res = await api.deleteVerificationDocuments(userId);
      if (res && res.success && res.data) {
        setUser(res.data);
        setFormData(res.data);
        mergeStoredUser(res.data);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Delete verification documents failed', e);
    } finally {
      setDeletingVerificationDocs(false);
    }
  };

  return (
    <AppLayout title={t('profile.accountTitle')} rootClass="layout-1">
      <NioSection className="pt-120 pt-lg-160">
        <Row className="justify-content-center">
          <Col lg={10}>
            <NioCard className="shadow-sm">
              <NioCard.Body className="p-4 p-lg-5">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
                  <h2 className="mb-0">{t('profile.profileTitle')}</h2>
                  <div className="d-flex gap-2">
                    {user.role === 'provider' && (
                      <NioButton
                        href="/profile/provider"
                        label={t('profile.providerSpace')}
                        className="btn-outline-primary btn-sm"
                        disabled={saving}
                      />
                    )}
                    {user.role === 'admin' && (
                      <NioButton
                        href="/admin/dashboard"
                        label={t('profile.adminDashboard')}
                        className="btn-outline-primary btn-sm"
                        disabled={saving}
                      />
                    )}
                    {!isEditing && (
                      <NioButton
                        onClick={handleStartEdit}
                        label={t('profile.editProfile')}
                        className="btn-outline-secondary btn-sm"
                        disabled={saving}
                      />
                    )}
                    {isEditing && (
                      <>
                        <NioButton
                          onClick={handleCancelEdit}
                          label={t('profile.cancel')}
                          className="btn-outline-secondary btn-sm"
                          disabled={saving}
                        />
                        <NioButton
                          onClick={handleSaveEdit}
                          label={saving ? t('profile.saving') : t('profile.save')}
                          className="btn-primary btn-sm"
                          disabled={saving}
                        />
                      </>
                    )}
                  </div>
                </div>
                <div className="mb-4 pb-3 border-bottom">
                  {((isEditing ? formData?.photo : user.photo)) ? (
                    <img
                      src={isEditing ? formData?.photo : user.photo}
                      alt={t('profile.profilePicture')}
                      className="rounded-circle mb-2"
                      style={{ width: 64, height: 64, objectFit: 'cover' }}
                    />
                  ) : (
                    <div
                      className="rounded-circle bg-primary bg-opacity-10 d-inline-flex align-items-center justify-content-center text-primary fw-bold mb-2"
                      style={{ width: 64, height: 64, fontSize: '1.5rem' }}
                    >
                      {displayName.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="mb-2">
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/*"
                      className="d-none"
                      onChange={handlePhotoChange}
                    />
                    <NioButton
                      onClick={handlePickPhoto}
                      label={user?.photo ? t('profile.changePhoto') : t('profile.addPhoto')}
                      className="btn-outline-primary btn-sm"
                      disabled={saving}
                    />
                  </div>
                  <h5 className="mb-0">{displayName}</h5>
                  {(user.civilite || user.role) && (
                    <span className="text-muted">
                      {[user.civilite, roleLabel].filter(Boolean).join(' · ')}
                    </span>
                  )}
                  {user.createdAt && (
                    <p className="text-muted small mb-0 mt-1">
                      {t('profile.registeredOnDate', { date: formatDate(user.createdAt) })}
                    </p>
                  )}
                </div>

                {user.role !== 'provider' && (isEditing || [user.civilite, user.name, user.lastName, user.email, user.phone].some(hasValue)) && (
                  <Section title={t('profile.identity')}>
                    <Field label={t('profile.civility')} value={user.civilite} name="civilite" />
                    <Field label={t('profile.firstName')} value={user.name} name="name" />
                    <Field label={t('profile.lastName')} value={user.lastName} name="lastName" />
                    <Field label={t('profile.email')} value={user.email} name="email" type="email" />
                    <Field label={t('profile.phone')} value={user.phone} name="phone" />
                  </Section>
                )}

                {(isEditing || hasValue(roleLabel) || (user.role === 'provider' && hasValue(userTypeLabel))) && (
                  <Section title={t('profile.roleAndType')}>
                    <Field label={t('profile.role')} value={roleLabel} />
                    {user.role === 'provider' && <Field label={t('profile.providerType')} value={userTypeLabel} />}
                  </Section>
                )}

                {user.role !== 'provider' && (isEditing || [user.region, user.city, user.address, user.location].some(hasValue)) && (
                  <Section title={t('profile.location')}>
                    <Field label={t('profile.region')} value={user.region} name="region" />
                    <Field label={t('profile.city')} value={user.city} name="city" />
                    <Field label={t('profile.address')} value={user.address} name="address" />
                    <Field label={t('profile.locationLabel')} value={user.location} name="location" />
                  </Section>
                )}

                {user.role === 'provider' && (
                  (isEditing || providerInfoHasValue || otherFieldsWithValue.length > 0) && (
                    <Section title={t('profile.providerInfo')}>
                      {/* Identité */}
                      <Field label={t('profile.civility')} value={user.civilite} name="civilite" />
                      <Field label={t('profile.firstName')} value={user.name} name="name" />
                      <Field label={t('profile.lastName')} value={user.lastName} name="lastName" />
                      <Field label={t('profile.email')} value={user.email} name="email" type="email" />
                      <Field label={t('profile.phone')} value={user.phone} name="phone" />

                      {/* Localisation */}
                      <Field label={t('profile.region')} value={user.region} name="region" />
                      <Field label={t('profile.city')} value={user.city} name="city" />
                      <Field label={t('profile.address')} value={user.address} name="address" />
                      <Field label={t('profile.locationLabel')} value={user.location} name="location" />

                      {/* Type / Métier */}
                      <Field label={t('profile.providerType')} value={userTypeLabel} />
                      <Field label={t('profile.service')} value={user.serviceId} name="serviceId" />
                      <Field label={t('profile.subService')} value={user.serviceCategory} name="serviceCategory" />
                      <Field label={t('profile.sector')} value={user.sector} name="sector" />
                      <Field label={t('profile.subSector')} value={user.subSector} name="subSector" />
                      <Field label={t('profile.creationYear')} value={user.creationYear} name="creationYear" />

                      {/* Identifiants entreprise */}
                      <Field label={t('profile.registrationNumber')} value={user.registrationNumber} name="registrationNumber" />
                      <Field label={t('profile.vatNumber')} value={user.vatNumber} name="vatNumber" />
                      <Field label={t('profile.cinNumber')} value={user.numCin} name="numCin" />
                      <Field label={t('profile.companyType')} value={user.companyType} name="companyType" />
                      <Field label={t('profile.legalForm')} value={user.legalForm} name="legalForm" />
                      <Field label={t('profile.businessName')} value={user.businessName} name="businessName" />

                      {/* Contenu de l'offre */}
                      <Field label={t('profile.experience')} value={user.experience} name="experience" />
                      <Field label={t('profile.skills')} value={user.skills} name="skills" />
                      <Field label={t('profile.qualifications')} value={user.qualifications} name="qualifications" />
                      <Field label={t('profile.description')} value={user.description} name="description" />
                      <Field label={t('profile.about')} value={user.about} name="about" />
                      <Field label={t('profile.clientCommitments')} value={user.clientCommitments} name="clientCommitments" />
                      <Field label={t('profile.pricing')} value={user.pricing} name="pricing" />
                      <Field label={t('profile.schedule')} value={user.schedule} name="schedule" />
                      <Field label={t('profile.coverageGuarantees')} value={user.coverageGuarantees} name="coverageGuarantees" />
                      <Field label={t('profile.serviceArea')} value={zoneIntervention} />
                      <Field label={t('profile.paymentMethod')} value={user.paymentMethod} name="paymentMethod" />
                      <Field label={t('profile.portfolio')} value={portfolioSummary} />

                      {/* Réseaux sociaux */}
                      <Field label={t('profile.linkedIn')} value={user.linkedin} name="linkedin" />
                      <Field label={t('profile.facebook')} value={user.facebook} name="facebook" />
                      <Field label={t('profile.twitter')} value={user.twitter} name="twitter" />
                      <Field label={t('profile.instagram')} value={user.instagram} name="instagram" />
                      <Field label={t('profile.website')} value={user.website} name="website" />
                    </Section>
                  )
                )}
                {user.role !== 'provider' && (isEditing || [user.linkedin, user.facebook, user.twitter, user.instagram, user.website].some(hasValue)) && (
                  <Section title={t('profile.socialNetworks')}>
                    <Field label={t('profile.linkedIn')} value={user.linkedin} name="linkedin" />
                    <Field label={t('profile.facebook')} value={user.facebook} name="facebook" />
                    <Field label={t('profile.twitter')} value={user.twitter} name="twitter" />
                    <Field label={t('profile.instagram')} value={user.instagram} name="instagram" />
                    <Field label={t('profile.website')} value={user.website} name="website" />
                  </Section>
                )}

                <div id="payments" className="mt-4 pt-3 border-top">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h5 className="mb-0" style={{ fontSize: '1rem', fontWeight: 700 }}>{t('profile.myPaymentsTitle')}</h5>
                    {user.role !== 'admin' && !showAllPayments && payments.length >= 5 && (
                      <NioButton
                        label={t('profile.viewAllPayments')}
                        className="btn-outline-primary btn-sm"
                        disabled={saving || paymentsLoading}
                        onClick={() => {
                          setShowAllPayments(true);
                          loadPayments(user.email, 100);
                        }}
                      />
                    )}
                  </div>
                  {paymentsLoading && (
                    <p className="text-muted small">{t('profile.paymentsLoading')}</p>
                  )}
                  {paymentsError && (
                    <p className="text-danger small">{paymentsError}</p>
                  )}
                  {!paymentsLoading && !paymentsError && payments.length === 0 && (
                    <p className="text-muted small">{t('profile.noPayments')}</p>
                  )}
                  {!paymentsLoading && payments.length > 0 && (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #e5e7eb', color: '#6b7280', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.04em' }}>
                            <th style={{ padding: '0.4rem 0.6rem', textAlign: 'left', fontWeight: 600 }}>{t('profile.paymentDate')}</th>
                            <th style={{ padding: '0.4rem 0.6rem', textAlign: 'left', fontWeight: 600 }}>{t('profile.paymentDesc')}</th>
                            <th style={{ padding: '0.4rem 0.6rem', textAlign: 'right', fontWeight: 600 }}>{t('profile.paymentAmount')}</th>
                            <th style={{ padding: '0.4rem 0.6rem', textAlign: 'center', fontWeight: 600 }}>{t('profile.paymentStatus')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payments.map((p) => {
                            const statusColors = {
                              completed: { bg: '#dcfce7', color: '#15803d' },
                              failed: { bg: '#fee2e2', color: '#dc2626' },
                              expired: { bg: '#f3f4f6', color: '#6b7280' },
                              on_site: { bg: '#dbeafe', color: '#1d4ed8' },
                              pending: { bg: '#fef9c3', color: '#92400e' },
                            };
                            const sc = statusColors[p.status] || statusColors.pending;
                            const dateStr = p.createdAt
                              ? new Date(p.createdAt).toLocaleDateString(language, { dateStyle: 'medium' })
                              : '-';
                            const c = String(p.currency || 'EUR').toUpperCase();
                            const decimals = c === 'TND' ? 3 : 2;
                            const amtStr = `${Number(p.amount || 0).toFixed(decimals)} ${c}`;
                            return (
                              <tr key={p._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                <td style={{ padding: '0.5rem 0.6rem', color: '#374151' }}>{dateStr}</td>
                                <td style={{ padding: '0.5rem 0.6rem', color: '#374151', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.description || '-'}</td>
                                <td style={{ padding: '0.5rem 0.6rem', textAlign: 'right', fontWeight: 600, color: '#0f172a' }}>{amtStr}</td>
                                <td style={{ padding: '0.5rem 0.6rem', textAlign: 'center' }}>
                                  <span style={{ display: 'inline-block', background: sc.bg, color: sc.color, borderRadius: '999px', padding: '0.15rem 0.6rem', fontWeight: 600, fontSize: '0.75rem' }}>
                                    {t(`paymentReturn.status${({ completed: 'Completed', pending: 'Pending', failed: 'Failed', expired: 'Expired', on_site: 'OnSite' })[p.status] || 'Pending'}`) || p.status}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </NioCard.Body>
            </NioCard>
          </Col>
        </Row>
      </NioSection>
    </AppLayout>
  );
}
